import {
    AddressForm,
    BackButton,
    Container,
    convertFromBoolean,
    convertToBoolean,
    Divider,
    Form,
    FormDropDown,
    FormInput,
    FormRadio,
    Header,
    NameForm,
    PersonForm,
    SummaryGroup,
    setAttributeEntity,
    useAlignments,
    useSizes,
} from 'metrix-common3';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ProfileResidentCancellationReason from './CancellationReason';
import ProfileResidentClaim from './Claim';
import ProfileResidentCriminalConviction from './CriminalConviction';

const ProfileResident = (props) => {
    const sizes = useSizes();
    const alignments = useAlignments();
    const intl = useIntl();

    const [mode, setMode] = useState('resident');
    const [current, setCurrent] = useState(null);

    const getResidentCriminalConvictions = () => {
        const residentCriminalConvictions = [];

        if (props.resident?.criminalConvictions && props.resident.criminalConvictions.length > 0) {
            props.resident.criminalConvictions.forEach((criminalConviction) => {
                const option = { code: criminalConviction.reasonDescription, value: criminalConviction.guid };

                let criminalConvictionName = intl.formatMessage({
                    id: 'Profile_Residents_Resident.newCriminalConviction',
                });

                if (criminalConviction.reason?.value) {
                    criminalConvictionName = `${criminalConviction.reason.value}`;
                }

                option.elements = [];

                option.elements.push({ code: 'Label', value: criminalConvictionName });

                residentCriminalConvictions.push(option);
            });
        }

        return residentCriminalConvictions;
    };

    const getResidentClaims = () => {
        const residentClaims = [];

        if (props.resident?.claims && props.resident.claims.length > 0) {
            props.resident.claims.forEach((claim) => {
                const option = { code: claim.reasonDescription, value: claim.guid };

                let claimName = intl.formatMessage({ id: 'Profile_Residents_Resident.newClaim' });

                if (claim.reason?.value) {
                    claimName = `${claim.reason.value}`;
                }

                option.elements = [];

                option.elements.push({ code: 'Label', value: claimName });

                residentClaims.push(option);
            });
        }

        return residentClaims;
    };

    const getResidentCancellationReasons = () => {
        const residentCancellationReasons = [];

        if (props.resident?.cancellationReasons && props.resident.cancellationReasons.length > 0) {
            props.resident.cancellationReasons.forEach((cancellationReason) => {
                const option = { code: cancellationReason.reasonDescription, value: cancellationReason.guid };

                let cancellationReasonName = intl.formatMessage({
                    id: 'Profile_Residents_Resident.newCancellationReason',
                });

                if (cancellationReason.reason?.value) {
                    cancellationReasonName = `${cancellationReason.reason.value}`;
                }

                option.elements = [];

                option.elements.push({ code: 'Label', value: cancellationReasonName });

                residentCancellationReasons.push(option);
            });
        }

        return residentCancellationReasons;
    };

    const getResidentName = () => {
        let residentName = null;

        if (
            props.resident?.party?.name &&
            (props.resident.party.name.firstName || props.resident.party.name.lastName)
        ) {
            residentName = `${props.resident.party.name.firstName} ${props.resident.party.name.lastName}`;
        }

        return residentName;
    };

    const residentName =
        getResidentName() ||
        intl.formatMessage({ id: 'Profile_Residents_Resident.resident' }, { number: props.resident.number || '' });

    return (
        <React.Fragment>
            {mode === 'resident' && (
                <React.Fragment>
                    <BackButton clickEvent={props.backEvent} />
                    <Divider top={sizes.md} />
                    <Header size={sizes.md} textAlign={alignments.left} text={residentName} />
                    <Form
                        label={<FormattedMessage id='Profile_Residents_Resident.residentDetails' />}
                        allowEdit={props.allowEdit}
                        saveEvent={() => props.doUpsertPolicy()}
                        cancelEvent={() => props.doGetPolicy()}
                    >
                        <FormInput
                            label={<FormattedMessage id='Profile_Residents_Resident.number' />}
                            value={props.resident?.number}
                            changeEvent={(value) =>
                                props.doReducePolicyElement({ code: `${props.dotProp}number`, value })
                            }
                            allowEdit
                        />
                        <FormDropDown
                            label={<FormattedMessage id='Profile_Residents_Resident.type' />}
                            value={props.resident?.applicantType}
                            references={props.reference.references?.Applicant}
                            referencesEvent={() => props.reference.doGetReferences('Applicant')}
                            changeEvent={(value) =>
                                setAttributeEntity(`${props.dotProp}applicantType`, value, props.doReducePolicyElement)
                            }
                            allowEdit
                        />
                        <FormRadio
                            label={<FormattedMessage id='Profile_Residents_Resident.applicant' />}
                            value={props.resident && { code: convertFromBoolean(props.resident.isInsured) }}
                            references={props.reference.references?.YesNo}
                            referencesEvent={() => props.reference.doGetReferences('YesNo')}
                            changeEvent={(value) =>
                                props.doReducePolicyElement({
                                    code: `${props.dotProp}isInsured`,
                                    value: convertToBoolean(value.code),
                                })
                            }
                            allowEdit
                        />
                        <FormRadio
                            label={<FormattedMessage id='Profile_Residents_Resident.coApplicant' />}
                            value={props.resident && { code: convertFromBoolean(props.resident.isCoInsured) }}
                            references={props.reference.references?.YesNo}
                            referencesEvent={() => props.reference.doGetReferences('YesNo')}
                            changeEvent={(value) =>
                                props.doReducePolicyElement({
                                    code: `${props.dotProp}isCoInsured`,
                                    value: convertToBoolean(value.code),
                                })
                            }
                            allowEdit
                        />
                        <FormDropDown
                            label={<FormattedMessage id='Profile_Residents_Resident.relationshipToApplicant' />}
                            value={props.resident?.relationshipToInsured}
                            references={props.reference.references?.RelationToApplicant}
                            referencesEvent={() => props.reference.doGetReferences('RelationToApplicant')}
                            changeEvent={(value) =>
                                setAttributeEntity(
                                    `${props.dotProp}relationshipToInsured`,
                                    value,
                                    props.doReducePolicyElement,
                                )
                            }
                            allowEdit
                        />
                        <FormRadio
                            label={<FormattedMessage id='Profile_Residents_Resident.bankruptcy' />}
                            value={props.resident && { code: convertFromBoolean(props.resident.bankruptcy) }}
                            references={props.reference.references?.YesNo}
                            referencesEvent={() => props.reference.doGetReferences('YesNo')}
                            changeEvent={(value) =>
                                props.doReducePolicyElement({
                                    code: `${props.dotProp}bankruptcy`,
                                    value: convertToBoolean(value.code),
                                })
                            }
                            allowEdit
                        />
                        <FormInput
                            label={<FormattedMessage id='Profile_Residents_Resident.yearsContinuouslyInsured' />}
                            value={props.resident?.yearsContinuouslyInsured}
                            changeEvent={(value) =>
                                props.doReducePolicyElement({ code: `${props.dotProp}yearsContinuouslyInsured`, value })
                            }
                            allowEdit
                        />
                        <FormInput
                            label={<FormattedMessage id='Profile_Residents_Resident.yearsInsuredAtCurrentAddress' />}
                            value={props.resident?.yearsContinuouslyInsuredCurrentAddress}
                            changeEvent={(value) =>
                                props.doReducePolicyElement({
                                    code: `${props.dotProp}yearsContinuouslyInsuredCurrentAddress`,
                                    value,
                                })
                            }
                            allowEdit
                        />
                        <FormInput
                            label={<FormattedMessage id='Profile_Residents_Resident.yearsAtCurrentAddress' />}
                            value={props.resident?.addressTimeLivedAt}
                            changeEvent={(value) =>
                                props.doReducePolicyElement({ code: `${props.dotProp}addressTimeLivedAt`, value })
                            }
                            allowEdit
                        />
                        <FormInput
                            label={<FormattedMessage id='Profile_Residents_Resident.numberOfLosses' />}
                            value={props.resident?.numberOfLosses}
                            changeEvent={(value) =>
                                props.doReducePolicyElement({ code: `${props.dotProp}numberOfLosses`, value })
                            }
                            allowEdit
                        />
                        <FormInput
                            label={<FormattedMessage id='Profile_Residents_Resident.numberOfYearsForLosses' />}
                            value={props.resident?.numberOfYearsForLosses}
                            changeEvent={(value) =>
                                props.doReducePolicyElement({ code: `${props.dotProp}numberOfYearsForLosses`, value })
                            }
                            allowEdit
                        />
                        <FormRadio
                            label={<FormattedMessage id='Profile_Residents_Resident.lossWithin3Years' />}
                            value={
                                props.resident && { code: convertFromBoolean(props.resident.lossWithinLastThreeYears) }
                            }
                            references={props.reference.references?.YesNo}
                            referencesEvent={() => props.reference.doGetReferences('YesNo')}
                            changeEvent={(value) =>
                                props.doReducePolicyElement({
                                    code: `${props.dotProp}lossWithinLastThreeYears`,
                                    value: convertToBoolean(value.code),
                                })
                            }
                            allowEdit
                        />
                    </Form>
                    {props.resident?.addressTimeLivedAt && props.resident.addressTimeLivedAt <= 3 && (
                        <AddressForm
                            header={<FormattedMessage id='Profile_Residents_Resident.previousAddressDetails' />}
                            label={<FormattedMessage id='Profile_Residents_Resident.locationDetails' />}
                            saveEvent={props.doUpsertPolicy}
                            cancelEvent={props.doGetPolicy}
                            reference={props.reference}
                            changeEvent={(dotProp, value) =>
                                props.doReducePolicyElement({
                                    code: `${props.dotProp}previousAddress.${dotProp}`,
                                    value,
                                })
                            }
                            allowEdit={props.allowEdit}
                            address={props.resident?.previousAddress}
                        />
                    )}
                    <NameForm
                        label={<FormattedMessage id='Profile_Residents_Resident.name' />}
                        name={props.resident?.party?.name}
                        person={props.resident?.person}
                        saveEvent={props.doUpsertPolicy}
                        cancelEvent={props.doGetPolicy}
                        reference={props.reference}
                        changeEvent={(dotProp, value) =>
                            props.doReducePolicyElement({
                                code:
                                    dotProp === 'person.gender'
                                        ? `${props.dotProp}${dotProp}`
                                        : `${props.dotProp}party.name.${dotProp}`,
                                value,
                            })
                        }
                        allowEdit={props.allowEdit}
                    />
                    <PersonForm
                        label={<FormattedMessage id='Profile_Residents_Resident.person' />}
                        person={props.resident?.person}
                        saveEvent={props.doUpsertPolicy}
                        cancelEvent={props.doGetPolicy}
                        reference={props.reference}
                        changeEvent={(dotProp, value) =>
                            props.doReducePolicyElement({ code: `${props.dotProp}person.${dotProp}`, value })
                        }
                        allowEdit={props.allowEdit}
                        culture={props.context.session.culture}
                    />
                    <Divider top={sizes.xl} />
                    <Header
                        size={sizes.lg}
                        textAlign={alignments.left}
                        text={<FormattedMessage id='Profile_Residents_Resident.criminalConvictions' />}
                    />
                    <Container top={sizes.xl} bottom={sizes.xl}>
                        <SummaryGroup
                            allowAdd={props.allowEdit}
                            addEvent={() => {
                                props.doExecutePolicyAction({ code: 'CREATERESIDENTCRIMINALCONVICTION' });
                            }}
                            allowEdit
                            editEvent={(criminalConviction) => {
                                setCurrent(criminalConviction);
                                setMode('criminalConviction');
                            }}
                            allowDelete={props.allowEdit}
                            deleteEvent={(criminalConviction) => {
                                props.doReduceParameter({
                                    code: 'ResidentCriminalConviction',
                                    value: criminalConviction,
                                });
                                props.doExecutePolicyAction({ code: 'DELETERESIDENTCRIMINALCONVICTION' });
                                setCurrent(null);
                                setMode('resident');
                            }}
                            entities={getResidentCriminalConvictions()}
                            icon='criminalConviction'
                        />
                    </Container>
                    <Divider top={sizes.xl} />
                    <Header
                        size={sizes.lg}
                        textAlign={alignments.left}
                        text={<FormattedMessage id='Profile_Residents_Resident.claims' />}
                    />
                    <Container top={sizes.xl} bottom={sizes.xl}>
                        <SummaryGroup
                            allowAdd={props.allowEdit}
                            addEvent={() => {
                                props.doExecutePolicyAction({ code: 'CREATERESIDENTCLAIM' });
                            }}
                            allowEdit
                            editEvent={(claim) => {
                                setCurrent(claim);
                                setMode('claim');
                            }}
                            allowDelete={props.allowEdit}
                            deleteEvent={(claim) => {
                                props.doReduceParameter({ code: 'ResidentClaim', value: claim });
                                props.doExecutePolicyAction({ code: 'DELETERESIDENTCLAIM' });
                                setCurrent(null);
                                setMode('resident');
                            }}
                            entities={getResidentClaims()}
                            icon='claim'
                        />
                    </Container>
                    <Divider top={sizes.xl} />
                    <Header
                        size={sizes.lg}
                        textAlign={alignments.left}
                        text={<FormattedMessage id='Profile_Residents_Resident.cancellations' />}
                    />
                    <Container top={sizes.xl} bottom={sizes.xl}>
                        <SummaryGroup
                            allowAdd={props.allowEdit}
                            addEvent={() => {
                                props.doExecutePolicyAction({ code: 'CREATERESIDENTCANCELLATIONREASON' });
                            }}
                            allowEdit
                            editEvent={(cancellationReason) => {
                                setCurrent(cancellationReason);
                                setMode('cancellationReason');
                            }}
                            allowDelete={props.allowEdit}
                            deleteEvent={(cancellationReason) => {
                                props.doReduceParameter({
                                    code: 'ResidentCancellationReason',
                                    value: cancellationReason,
                                });
                                props.doExecutePolicyAction({ code: 'DELETERESIDENTCANCELLATIONREASON' });
                                setCurrent(null);
                                setMode('resident');
                            }}
                            entities={getResidentCancellationReasons()}
                            icon='cancellation'
                        />
                    </Container>
                </React.Fragment>
            )}
            {mode === 'criminalConviction' && (
                <ProfileResidentCriminalConviction
                    criminalConviction={props.resident.criminalConvictions.find((x) => x.guid === current)}
                    dotProp={`${props.dotProp}criminalConvictions.${props.resident.criminalConvictions.findIndex((x) => x.guid === current)}.`}
                    backEvent={() => {
                        setMode('resident');
                        setCurrent(null);
                    }}
                    doUpsertPolicy={props.doUpsertPolicy}
                    doGetPolicy={props.doGetPolicy}
                    doReducePolicyElement={props.doReducePolicyElement}
                    reference={props.reference}
                    context={props.context}
                    allowEdit={props.allowEdit}
                />
            )}
            {mode === 'claim' && (
                <ProfileResidentClaim
                    claim={props.resident.claims.find((x) => x.guid === current)}
                    dotProp={`${props.dotProp}claims.${props.resident.claims.findIndex((x) => x.guid === current)}.`}
                    backEvent={() => {
                        setMode('resident');
                        setCurrent(null);
                    }}
                    doUpsertPolicy={props.doUpsertPolicy}
                    doGetPolicy={props.doGetPolicy}
                    doReducePolicyElement={props.doReducePolicyElement}
                    reference={props.reference}
                    context={props.context}
                    allowEdit={props.allowEdit}
                />
            )}
            {mode === 'cancellationReason' && (
                <ProfileResidentCancellationReason
                    cancellationReason={props.resident.cancellationReasons.find((x) => x.guid === current)}
                    dotProp={`${props.dotProp}cancellationReasons.${props.resident.cancellationReasons.findIndex((x) => x.guid === current)}.`}
                    backEvent={() => {
                        setMode('resident');
                        setCurrent(null);
                    }}
                    doUpsertPolicy={props.doUpsertPolicy}
                    doGetPolicy={props.doGetPolicy}
                    doReducePolicyElement={props.doReducePolicyElement}
                    reference={props.reference}
                    context={props.context}
                    allowEdit={props.allowEdit}
                />
            )}
        </React.Fragment>
    );
};

export default ProfileResident;
